import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions<T = unknown> {
  /** Async function to persist the data */
  onSave: (data: T) => Promise<void>;
  /** Debounce delay in milliseconds (default: 3000) */
  debounceMs?: number;
  /** Whether autosave is enabled (default: true) */
  enabled?: boolean;
  /**
   * If true, flushes any pending save when the component unmounts.
   * Useful for tab panels or modals where the component may unmount
   * before the debounce timer fires.
   *
   * Note: The flush is fire-and-forget since cleanup can't be async.
   * Errors are logged but won't update status (component is unmounting).
   */
  flushOnUnmount?: boolean;
}

export const useAutoSave = <T = unknown>({
  onSave,
  debounceMs = 3000,
  enabled = true,
  flushOnUnmount = false,
}: UseAutoSaveOptions<T>) => {
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveTailRef = useRef<Promise<void>>(Promise.resolve());
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Track pending data for flush on unmount
  const pendingDataRef = useRef<T | null>(null);
  const hasPendingRef = useRef(false);

  // Use refs to always have the latest values (fixes stale closure issues)
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const flushOnUnmountRef = useRef(flushOnUnmount);
  flushOnUnmountRef.current = flushOnUnmount;

  // Clean up timeouts on unmount, with optional flush
  useEffect(() => {
    return () => {
      // Clear pending timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // Flush pending data if enabled and there's something to save
      if (flushOnUnmountRef.current && hasPendingRef.current && pendingDataRef.current !== null) {
        // Fire and forget - can't await in cleanup
        // Use the ref to get the latest onSave function
        onSaveRef.current(pendingDataRef.current).catch((error: unknown) => {
          console.error('Auto-save flush on unmount failed:', error);
        });
      }

      // Clear status timeout
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
        statusTimeoutRef.current = null;
      }
    };
  }, []);

  const performSave = useCallback(async (data: T) => {
    if (!enabledRef.current) return;

    // Clear pending state since we're saving now
    hasPendingRef.current = false;
    pendingDataRef.current = null;

    setStatus('saving');

    const save = saveTailRef.current.then(() => onSaveRef.current(data));
    saveTailRef.current = save.then(
      () => undefined,
      () => undefined,
    );

    try {
      await save;
      setStatus('saved');
      setLastSavedAt(new Date());

      // Reset to idle after showing saved state
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
      statusTimeoutRef.current = setTimeout(() => {
        setStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setStatus('error');
      throw error;
    }
  }, []);

  const triggerAutoSave = useCallback(
    (data: T) => {
      // Track pending data for potential flush on unmount
      pendingDataRef.current = data;
      hasPendingRef.current = true;

      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout - enabled check happens in performSave using ref
      // to avoid stale closure issues
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        void performSave(data).catch(() => undefined);
      }, debounceMs);
    },
    [performSave, debounceMs],
  );

  const saveImmediately = useCallback(
    (data: T) => {
      if (!enabled) return;

      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // Save immediately
      void performSave(data).catch(() => undefined);
    },
    [performSave, enabled],
  );

  /**
   * Cancel a pending debounced save and wait for this save to finish. Saves
   * are serialized, so this write also waits for any request already in
   * flight and cannot be overtaken by an older payload.
   */
  const saveImmediatelyAndWait = useCallback(
    (data: T): Promise<void> => {
      if (!enabledRef.current) return Promise.resolve();

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      return performSave(data);
    },
    [performSave],
  );

  /**
   * Check if there's a pending save that hasn't been flushed yet.
   * Useful for showing "unsaved changes" warnings.
   */
  const hasPendingSave = useCallback(() => hasPendingRef.current, []);

  /**
   * Cancel any pending save without flushing.
   * Useful when discarding changes.
   */
  const cancelPendingSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingDataRef.current = null;
    hasPendingRef.current = false;
  }, []);

  return {
    triggerAutoSave,
    saveImmediately,
    saveImmediatelyAndWait,
    hasPendingSave,
    cancelPendingSave,
    status,
    lastSavedAt,
  };
};
